import AutorenewIcon from "@mui/icons-material/Autorenew";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
} from "@mui/material";
import { Box } from "@mui/system";
import type {
  AttachedDocument,
  AttachedDocumentType,
} from "adl-gen/ferovinum/app/uploads";
import type React from "react";
import { useState } from "react";
import { useConfirmationDialog } from "../../context/global-dialog/use-dialog";

export interface DocumentDownloaderButtonProps {
  attachedDocuments: AttachedDocument[];
  downloadDocument: (doc: AttachedDocument) => Promise<void>;
  editGeneratedDocument?: (doc: AttachedDocument) => void;
  generateDocument?: (invoiceId: string) => Promise<void>;
  generateAWCOrderAttachment?: () => Promise<void>;
  // userIsAdmin: boolean;
  // appAdminUIHref: string;

  // We can't include app service here to get the actual type because this is a
  // component and components cannot include anything from the app-client
  // appService: any;
}

export const DocumentDownloader = ({
  attachedDocuments,
  downloadDocument,
  editGeneratedDocument,
  generateDocument,
  generateAWCOrderAttachment,
}: DocumentDownloaderButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<
    Record<string, AttachedDocument | null>
  >({} as Record<AttachedDocumentType, AttachedDocument | null>);
  const documents: AttachedDocument[] = generateAWCOrderAttachment
    ? [
        ...attachedDocuments,
        {
          documentType: "AwcOrderAttachment",
          storedFileId: "",
          invoiceId: null,
          page: 1,
        },
      ]
    : attachedDocuments;
  const { showConfirmationDialog } = useConfirmationDialog();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedFiles(
      {} as Record<AttachedDocumentType, AttachedDocument | null>,
    );
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    doc: AttachedDocument,
  ) => {
    const key = doc.page ? doc.documentType + doc.page : doc.documentType;
    setSelectedFiles({
      ...selectedFiles,
      [key]: event.target.checked ? doc : null,
    });
  };

  const handleDownload = async () => {
    const selectedDocuments = Object.values(selectedFiles).filter(
      (file): file is AttachedDocument => file !== null,
    );

    for (const attachedDocument of selectedDocuments) {
      if (
        attachedDocument.documentType === "AwcOrderAttachment" &&
        generateAWCOrderAttachment
      ) {
        await generateAWCOrderAttachment();
      } else {
        await downloadDocument(attachedDocument);
      }
    }

    handleClose();
  };

  const regenerateDocument =
    generateDocument &&
    (async (attachedDocument: AttachedDocument) => {
      if (attachedDocument.invoiceId) {
        const submit = await showConfirmationDialog({
          title:
            "Are you sure you want to regenerate this invoice? The previous invoice will be replaced and emails will be resent to recipients associated to the organization.",
          confirmAction: {
            onClick: async () => true,
          },
        });

        if (submit.kind === "ok") {
          await generateDocument(attachedDocument.invoiceId);
        }
      }
    });

  const isAnyCheckboxSelected = Object.values(selectedFiles).some(
    (file) => file !== null,
  );
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  if (!documents.length) {
    return <div />;
  }

  return (
    <div>
      <Button
        onClick={handleClick}
        variant={"outlined"}
        startIcon={<DownloadIcon />}
      >
        Supporting Documents
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: -10,
          horizontal: "left",
        }}
      >
        <Box
          sx={{
            padding: "16px 16px 0 16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {documents.map((doc) => {
            const key: string = doc.page
              ? doc.documentType + doc.page
              : doc.documentType; // Define key variable here
            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  whiteSpace: "nowrap",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(selectedFiles[key])}
                      onChange={(event) => handleCheckboxChange(event, doc)}
                    />
                  }
                  label={getLabel(doc)}
                />

                {doc.invoiceId !== null && (
                  <Box>
                    {editGeneratedDocument && (
                      <IconButton
                        onClick={() => editGeneratedDocument(doc)}
                        sx={{ marginRight: "8px" }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}

                    {regenerateDocument && (
                      <IconButton onClick={() => regenerateDocument(doc)}>
                        <AutorenewIcon />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            paddingTop: "4px",
            paddingBottom: "10px",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{
              borderRadius: "25px",
              padding: "8px 16px", // Add padding to the button
            }}
            onClick={handleDownload}
            disabled={!isAnyCheckboxSelected}
          >
            Download
          </Button>
        </Box>
      </Popover>
    </div>
  );
};

function getLabel(attachedDocument: AttachedDocument) {
  switch (attachedDocument.documentType) {
    case "AwcOrderAttachment":
      return "AWC Order Attachment";
    case "SaleInvoice":
      return "Sale Invoice";
    case "DepositInvoice":
      return "Deposit Invoice";
    case "StockRevaluationInvoice":
      return "Stock Revaluation Invoice";
    case "DealRenewalInvoice":
      return "Deal Renewal Invoice";
    case "PurchaseOrder":
      return "Purchase Order";
    case "ProductionLossInvoice":
      return "Production Loss Invoice";
    case "DeliveryLossInvoice":
      return "Delivery Loss Invoice";
    case "PurchaseInvoice":
      return "Purchase Invoice";
    case "DeliveryOrder":
      return "Delivery Order";
    case "ContractNote":
      return "Contract Note";
    case "TradeSalesPurchaseOrder":
      return "Purchase Order";
    case "NominatedPurchaserOutstandingBalanceSummary":
      return "Outstanding Balance Summary";
    case "DeliveryInstructionDocument":
      return `Delivery Document Page ${attachedDocument.page}`;
    case "ProformaPurchaseInvoice":
      return "Pro forma Invoice";
    case "VoidNewDealInvoice":
      return "Void Deal Invoice";
    default:
      return "PDF";
  }
}
